import { useMemo } from 'react'
import { Award, FileText } from 'lucide-react'
import Button from '../../components/Button'
import Checkbox from '../../components/Checkbox'
import DataTable from '../../components/DataTable'
import { kit as K } from '../../components/strings'
import { strings as S } from './strings'
import './print.css'
import { displayDate } from '../../lib/dates'

const count = (value) => value.toLocaleString('en-US')

/*
 * The roster and its two downloads, drawn from `useIssuedCertificates` — shared by
 * طباعة الشهادات and the programme report's certificates panel.
 */

/** Two downloads, and they are not the same thing: the CERTIFICATES, and the ROSTER of who they were printed for. */
export function IssuedCertificatesActions({ certificates }) {
  const idle = certificates.busy || certificates.chosen.length === 0

  return (
    <>
      <Button disabled={idle} onClick={certificates.downloadCertificates}>
        <Award size={16} strokeWidth={1.75} />
        {S.downloadCertificates}
      </Button>

      <Button variant="soft" disabled={idle} onClick={certificates.downloadList}>
        <FileText size={16} strokeWidth={1.75} />
        {S.downloadList}
      </Button>
    </>
  )
}

/**
 * The roster. A click on a row ticks it; "select all" ticks the whole list.
 *
 * @param {string} resetKey  the programme and the dates — the filter sits outside the table,
 *   so page 4 of one programme's roster must not stay page 4 of another's.
 */
export function IssuedCertificatesTable({ certificates, resetKey }) {
  const { rows, loading, note, chosen, isSelected, toggle, selectAll } = certificates

  /* Blank for every row when the programme has no validity, or keeps the date from
     administrators — then the two columns say nothing and are left out. */
  const showsExpiry = rows.some((row) => row.expiresOn)

  const columns = useMemo(
    () => [
      {
        key: 'counter',
        header: S.columns.index,
        width: 56,
        align: 'center',
        render: (row) => <span className="tabular">{row.counter}</span>,
      },
      { key: 'username', header: S.columns.username, render: (row) => <bdi>{row.username}</bdi> },
      { key: 'name', header: S.columns.name, render: (row) => <bdi>{row.name}</bdi> },
      {
        key: 'email',
        header: S.columns.email,
        render: (row) => (
          <span dir="ltr" className="print-cert__mail">
            {row.email}
          </span>
        ),
      },
      { key: 'group', header: S.columns.group, render: (row) => <bdi>{row.group}</bdi> },
      {
        key: 'issuedOn',
        header: S.columns.issuedOn,
        render: (row) => (
          <span dir="ltr" className="print-cert__date">
            {displayDate(row.issuedOn)}
          </span>
        ),
      },
      ...(showsExpiry
        ? [
            {
              key: 'expiresOn',
              header: S.columns.expiresOn,
              render: (row) => (
                <span dir="ltr" className="print-cert__date">
                  {displayDate(row.expiresOn)}
                </span>
              ),
            },
            {
              key: 'daysLeft',
              header: S.columns.daysLeft,
              /* The text can be a word rather than a number, so the raw span does the sorting. */
              value: (row) => row.daysLeftValue,
              render: (row) => <bdi>{row.daysLeft}</bdi>,
            },
          ]
        : []),
    ],
    [showsExpiry],
  )

  const all = rows.length > 0 && chosen.length === rows.length
  const some = chosen.length > 0 && !all

  return (
    <DataTable
      columns={columns}
      rows={rows}
      loading={loading}
      controls={false}
      resetKey={resetKey}
      getRowId={(row) => row.id}
      onRowClick={toggle}
      isRowSelected={isSelected}
      emptyMessage={note || S.empty}
      toolbar={
        rows.length > 0 && (
          <span className="print-cert__select">
            <Checkbox checked={all} indeterminate={some} onChange={selectAll}>
              {S.selectAll}
            </Checkbox>
            <span className={`print-cert__count${chosen.length ? ' has-some' : ''}`}>
              <strong className="tabular">{count(chosen.length)}</strong>
              <span className="tabular">{K.pickerOf(count(rows.length))}</span>
            </span>
          </span>
        )
      }
    />
  )
}
